import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { BaseResponse } from '../models/rest/baseResponse';
import {
  ICreateNexParkSipDeviceResponse,
  ICreateNexParkSipUserResponse,
  INexParkSipDeviceCredentials,
  IGetNexParkSipDevicesResponse,
  IGetNexParkSipQueuesResponse,
  IGetNexParkSipUsersResponse,
  IValidatePasswordTokenResponse,
} from '../models/nexpark-sip-provisioning';

export type SipProvisioningScope = 'voicePlatform' | 'mobilePhone';

@Injectable({ providedIn: 'root' })
export class SipProvisioningService {
  private readonly basePath = 'NexParkSipProvisioning';

  constructor(private http: HttpClient) { }

  getUsers(contactId: number, scope: SipProvisioningScope): Observable<IGetNexParkSipUsersResponse> {
    return this.http.get<IGetNexParkSipUsersResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Users`);
  }

  createUser(
    contactId: number,
    firstName: string,
    lastName: string,
    email: string,
    scope: SipProvisioningScope,
  ): Observable<ICreateNexParkSipUserResponse> {
    return this.http.post<ICreateNexParkSipUserResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Users`, {
      FirstName: firstName,
      LastName: lastName,
      Email: email,
    });
  }

  createDevice(contactId: number, userId: string, scope: SipProvisioningScope): Observable<ICreateNexParkSipDeviceResponse> {
    return this.http.post<ICreateNexParkSipDeviceResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Devices`, { UserId: userId });
  }

  getQueues(contactId: number, scope: SipProvisioningScope): Observable<IGetNexParkSipQueuesResponse> {
    return this.http.get<IGetNexParkSipQueuesResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Queues`);
  }

  getDevices(contactId: number, scope: SipProvisioningScope): Observable<IGetNexParkSipDevicesResponse> {
    return this.http.get<IGetNexParkSipDevicesResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Devices`);
  }

  assignUserToQueues(contactId: number, userId: string, queueIds: string[], scope: SipProvisioningScope): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Queues/Assign`, {
      UserId: userId,
      QueueIds: queueIds,
    });
  }

  removeUserFromQueue(contactId: number, queueId: string, userId: string, scope: SipProvisioningScope): Observable<BaseResponse> {
    return this.http.delete<BaseResponse>(`${this.getBasePath(scope)}/Contacts/${contactId}/Queues/${queueId}/Users/${userId}`);
  }

  getDeviceCredentials(contactId: number, deviceId: string, scope: SipProvisioningScope): Observable<INexParkSipDeviceCredentials> {
    return this.http.get<INexParkSipDeviceCredentials>(`${this.getBasePath(scope)}/Contacts/${contactId}/Devices/${deviceId}/Credentials`);
  }

  sendCredentials(contactId: number): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(`${this.basePath}/Contacts/${contactId}/SendCredentials`, {});
  }

  validateToken(token: string): Observable<IValidatePasswordTokenResponse> {
    return this.http.get<IValidatePasswordTokenResponse>(`${this.basePath}/PasswordToken/Validate`, {
      params: new HttpParams().set('Token', token),
    });
  }

  setPassword(token: string, password: string): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(`${this.basePath}/PasswordToken/SetPassword`, { Token: token, Password: password });
  }

  private getBasePath(scope: SipProvisioningScope): string {
    return scope === 'mobilePhone' ? `${this.basePath}/Mobile` : this.basePath;
  }
}
